import React, { useEffect, useRef } from 'react';
import { Order } from '../../lib/types';

interface ThreeDotMenuProps {
  order: Order;
  anchor: { top: number; right: number; flipUp: boolean; btnTop: number };
  canStock: boolean;
  isStocked: boolean;
  onEdit: (order: Order) => void;
  onViewHistory: (order: Order) => void;
  onStock: (order: Order) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

export function ThreeDotMenu({
  order, anchor, canStock, isStocked,
  onEdit, onViewHistory, onStock, onDelete, onClose,
}: ThreeDotMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    const onScroll = () => onClose();
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="menu"
      style={{
        position: 'fixed',
        top: anchor.flipUp ? anchor.btnTop : anchor.top,
        right: anchor.right,
        minWidth: 190,
        transform: anchor.flipUp ? 'translateY(-100%)' : 'none',
      }}
    >
      <button className="menu-item" onClick={() => { onEdit(order); onClose(); }}>
        <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.4">
          <path d="M9.5 3.5l3 3M3 10.5L10 3.5l3 3L6 13.5H3v-3z" />
        </svg>
        Edit order
      </button>
      <button className="menu-item" onClick={() => { onViewHistory(order); onClose(); }}>
        <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.4">
          <circle cx="8" cy="8" r="6" />
          <path d="M8 5v3.5l2.5 1.5" />
        </svg>
        View history
      </button>
      {canStock && (
        <button className="menu-item is-active" onClick={() => { onStock(order); onClose(); }}>
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.4">
            <rect x="2" y="5" width="12" height="9" rx="2" />
            <path d="M5 5V3.5C5 2.7 5.7 2 6.5 2h3c.8 0 1.5.7 1.5 1.5V5" />
            <path d="M8 8v3M6.5 9.5h3" />
          </svg>
          Add to inventory
        </button>
      )}
      {isStocked && (
        <div style={{ padding: '6px 12px', fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>
          Stocked
        </div>
      )}
      <div className="menu-sep" />
      <button className="menu-item is-danger" onClick={() => { onDelete(order.id); onClose(); }}>
        <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.4">
          <path d="M3 4h10M6 4V3h4v1M5 4v8.5c0 .5.5 1 1 1h4c.5 0 1-.5 1-1V4" />
        </svg>
        Delete order
      </button>
    </div>
  );
}
